import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, DEFAULT_CURRENCY_CODE, importProvidersFrom } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, Router, withEnabledBlockingInitialNavigation, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { CoreModule } from '@core';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import * as Sentry from '@sentry/angular';
import { MyInterceptor, SentryHttpInterceptor } from '@services';
import { provideToastr, ToastrModule } from 'ngx-toastr';
import { providePrimeNG } from 'primeng/config';
import { routerOptions, routes } from './app.routing';
import { GANTT_GLOBAL_CONFIG, GanttGlobalConfig, GanttI18nLocale, NgxGanttModule } from '@worktile/gantt';
import { MarkdownModule } from 'ngx-markdown';

const MyPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withInMemoryScrolling({ anchorScrolling: 'enabled' }),
      withEnabledBlockingInitialNavigation(),
      withRouterConfig(routerOptions),
      // withDebugTracing()
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: { preset: MyPreset, options: { darkModeSelector: '.app-dark' } },
      ripple: true,
      zIndex: { modal: 1100, overlay: 1000, menu: 900, tooltip: 1100 },
    }),
    provideToastr(),

    { provide: HTTP_INTERCEPTORS, useClass: MyInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SentryHttpInterceptor, multi: true },

    { provide: DEFAULT_CURRENCY_CODE, useValue: 'USD' },
    { provide: LocationStrategy, useClass: HashLocationStrategy },

    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },

    importProvidersFrom(NgxGanttModule),
    {
      provide: GANTT_GLOBAL_CONFIG,
      useValue: {
        locale: GanttI18nLocale.enUs,
        styleOptions: {
          headerHeight: 36,
          lineHeight: 44,
          barHeight: 24,
        },
      } as GanttGlobalConfig,
    },

    importProvidersFrom(MarkdownModule.forRoot()),
    importProvidersFrom(CoreModule),
    importProvidersFrom(NgIdleKeepaliveModule.forRoot()),
    importProvidersFrom(
      ToastrModule.forRoot({
        progressBar: true,
        progressAnimation: 'increasing',
        enableHtml: true,
        timeOut: navigator.webdriver ? 1 : 5000, // Immediately dismiss the toast in test mode
        extendedTimeOut: navigator.webdriver ? 1 : 1000, // Immediately dismiss the toast in test mode
      }),
    ),
  ],
};
